/**
 * The Overview page, kept to one screen: what the demo assistant is for, the two
 * kinds of UnlikelyAI capability running in it and the platform they were built
 * on, then three doors into the demo itself.
 */
import { ArrowRight } from "lucide-react";
import { useBranding } from "@targeted-support/ui/contexts";
import type { GuardrailTree, OutcomeTree } from "../types";

/**
 * A count that is still loading is shown as a placeholder bar, so the sentence
 * around it keeps its shape instead of appearing all at once.
 */
const Count = ({ value, isLoading }: { value: number; isLoading: boolean }) =>
  isLoading ? (
    <span
      className="inline-block h-3 w-4 animate-pulse rounded bg-brand-border align-baseline"
      aria-hidden={true}
    />
  ) : (
    <span className="font-semibold">{value}</span>
  );

/**
 * One kind of capability running in the demo, with where it fits in a turn.
 */
const CapabilityCard = ({
  title,
  count,
  isLoading,
  children,
}: {
  title: string;
  count: number;
  isLoading: boolean;
  children: React.ReactNode;
}) => (
  <div className="flex flex-1 flex-col gap-1.5 rounded-xl border border-unlikely/30 bg-unlikely/[0.03] px-5 py-4 text-left">
    <div className="flex items-baseline justify-between gap-2">
      <h2 className="text-sm font-semibold">{title}</h2>
      <span className="text-xs text-brand-muted">
        <Count value={count} isLoading={isLoading} /> in this demo
      </span>
    </div>
    <p className="text-xs leading-relaxed text-brand-muted">{children}</p>
  </div>
);

/**
 * A door into one page of the demo. The whole card is the button.
 */
const DoorCard = ({
  index,
  title,
  detail,
  linkLabel,
  onClick,
}: {
  index: number;
  title: string;
  detail: string;
  linkLabel: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className="group flex flex-1 flex-col gap-2.5 rounded-xl border border-brand-border bg-brand-card p-5 text-left transition-colors hover:border-unlikely/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-unlikely/40"
  >
    <span className="flex h-[22px] w-[22px] flex-none items-center justify-center rounded-full bg-unlikely text-[11px] font-semibold text-white">
      {index}
    </span>
    <span className="flex flex-1 flex-col gap-1">
      <span className="text-sm font-semibold">{title}</span>
      <span className="text-xs leading-relaxed text-brand-muted">{detail}</span>
    </span>
    <span className="inline-flex items-center gap-1 text-xs font-medium text-unlikely transition-colors group-hover:text-unlikely-hover">
      {linkLabel}
      <ArrowRight
        className="h-3 w-3 transition-transform group-hover:translate-x-0.5"
        aria-hidden={true}
      />
    </span>
  </button>
);

interface OverviewSectionProps {
  outcomes: OutcomeTree[];
  guardrails: GuardrailTree[];
  isLoadingOutcomes: boolean;
  isLoadingGuardrails: boolean;
  onNavigateToOutcomes: () => void;
  onNavigateToChat: () => void;
  onNavigateToHistory: () => void;
}

export const OverviewSection = ({
  outcomes,
  guardrails,
  isLoadingOutcomes,
  isLoadingGuardrails,
  onNavigateToOutcomes,
  onNavigateToChat,
  onNavigateToHistory,
}: OverviewSectionProps) => {
  const { name } = useBranding();

  const inputGuardrailCount = guardrails.filter(
    (guardrail) => guardrail.guardrailType === "INPUT"
  ).length;
  const outputGuardrailCount = guardrails.length - inputGuardrailCount;

  return (
    <section className="flex min-h-full flex-col items-center justify-center gap-10 p-12">
      <header className="flex flex-col items-center gap-3.5 text-center">
        <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-unlikely">
          Live demo · {name}
        </span>
        <h1 className="max-w-[640px] text-[32px] font-semibold leading-tight tracking-[-0.02em]">
          Understand the customer. Recommend the right product.
        </h1>
        <p className="max-w-[640px] text-[15px] leading-relaxed text-brand-muted text-pretty">
          That is targeted support: the assistant talks with a customer to
          establish their personal and financial situation, and works out
          whether it can recommend a product. Every rule {name} provided runs
          as an UnlikelyAI check, on every turn.
        </p>
      </header>

      <div className="flex w-full max-w-[880px] flex-col gap-2">
        <div className="flex flex-col gap-4 lg:flex-row">
          <CapabilityCard
            title="Outcomes"
            count={outcomes.length}
            isLoading={isLoadingOutcomes}
          >
            Where {name} wants a conversation to get to. Each one is
            re-evaluated on every turn: what is known, what to ask next, which
            products may be suggested.
          </CapabilityCard>
          <CapabilityCard
            title="Guardrails"
            count={guardrails.length}
            isLoading={isLoadingGuardrails}
          >
            What may not be said.{" "}
            <Count value={inputGuardrailCount} isLoading={isLoadingGuardrails} />{" "}
            {inputGuardrailCount === 1 ? "checks" : "check"} the customer's
            message on the way in,{" "}
            <Count
              value={outputGuardrailCount}
              isLoading={isLoadingGuardrails}
            />{" "}
            the agent's draft on the way out. A reply that fails is rewritten.
          </CapabilityCard>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-x-2.5 gap-y-1 rounded-xl border border-brand-border bg-brand-card px-5 py-3">
          <span className="whitespace-nowrap text-[10px] font-semibold uppercase tracking-[0.08em] text-unlikely">
            UnlikelyAI platform
          </span>
          <span className="text-xs text-brand-muted">
            Both are built here: each rule {name} provided becomes a
            capability, tested against worked examples, versioned and signed
            off.
          </span>
        </div>
      </div>

      <div className="flex w-full max-w-[880px] flex-col gap-4 lg:flex-row">
        <DoorCard
          index={1}
          title="Watch a turn live"
          detail="Chat with the assistant and see every check run beside the conversation."
          linkLabel="Open the chat"
          onClick={onNavigateToChat}
        />
        <DoorCard
          index={2}
          title="Read a decision back"
          detail="Pick any past conversation and see which checks ran and what each one answered."
          linkLabel="Open the history"
          onClick={onNavigateToHistory}
        />
        <DoorCard
          index={3}
          title="See a rule become a capability"
          detail={`One of ${name}'s rules, broken into questions answered the same way every time.`}
          linkLabel="Open the outcomes"
          onClick={onNavigateToOutcomes}
        />
      </div>

      <p className="text-xs text-brand-muted">
        The chat, the model and the wording stay {name}'s own. UnlikelyAI sits
        around them.
      </p>
    </section>
  );
};
